import { useEffect, useRef } from "react";
import {
  SimpleModal,
  SimpleModalDescription,
  SimpleModalFooter,
  SimpleModalHeader,
  SimpleModalTitle,
} from "../../../components/ui/simple-modal";

// Case-3 conflict prompt. The user already has a vote on this poll under their
// real account, and their just-submitted guest selections differ. They choose:
//   - "Keep previous": preserve the existing account vote (also the behaviour
//     for Escape / backdrop dismiss — a dismiss is NEVER a silent data-losing
//     abandon of the existing vote; see plan C9).
//   - "Replace": overwrite the existing vote with the guest selections.
//
// The safe default ("Keep previous") receives initial focus.
export default function GuestVoteConflictModal({
  open,
  existingSummary,
  onPreserve,
  onReplace,
}) {
  const keepRef = useRef(null);
  const replaceRef = useRef(null);

  // Focus the safe default ("Keep previous") when the modal opens, so keyboard
  // users land on the non-destructive choice first.
  useEffect(() => {
    if (!open) return;
    keepRef.current?.focus();
  }, [open]);

  // Lightweight focus trap: Tab / Shift+Tab cycle between the two actions so
  // focus stays inside the dialog while it is open.
  function handleTab(event) {
    if (event.key !== "Tab") return;
    const keep = keepRef.current;
    const replace = replaceRef.current;
    if (!keep || !replace) return;
    const focusables = [keep, replace];
    const idx = focusables.indexOf(document.activeElement);
    if (idx === -1) return;
    event.preventDefault();
    const next = event.shiftKey
      ? (idx - 1 + focusables.length) % focusables.length
      : (idx + 1) % focusables.length;
    focusables[next].focus();
  }

  // Escape / backdrop dismiss maps to keep-previous (the safe, non-destructive
  // choice). SimpleModal calls onOpenChange(false) on Escape and backdrop click.
  function handleOpenChange(nextOpen) {
    if (nextOpen) return;
    onPreserve?.();
  }

  return (
    <SimpleModal open={open} onOpenChange={handleOpenChange} contentClassName="max-w-md">
      <SimpleModalHeader>
        <SimpleModalTitle>You already voted on this poll</SimpleModalTitle>
        <SimpleModalDescription>
          {existingSummary
            ? `Your previous vote (${existingSummary}) will be kept unless you choose to replace it.`
            : "Your previous vote will be kept unless you choose to replace it."}
        </SimpleModalDescription>
      </SimpleModalHeader>
      <SimpleModalFooter onKeyDown={handleTab}>
        <button
          ref={keepRef}
          type="button"
          onClick={onPreserve}
          className="min-h-[44px] rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Keep previous
        </button>
        <button
          ref={replaceRef}
          type="button"
          onClick={onReplace}
          className="min-h-[44px] rounded-full bg-brand-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
        >
          Replace with my new vote
        </button>
      </SimpleModalFooter>
    </SimpleModal>
  );
}
